import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { getAdminDashboardStats } from "@/lib/admin/dashboard.functions";
import { getCheckoutMode } from "@/lib/checkout/checkout.functions";
import {
  getAdminAnalytics,
  type AnalyticsRange,
  type NamedCount,
} from "@/lib/admin/posthog-analytics.functions";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { StatCard } from "@/components/admin/StatCard";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export const Route = createFileRoute("/_admin/admin/")({
  component: DashboardPage,
});

const RANGE_LABELS: Record<AnalyticsRange, string> = {
  today: "Today",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  all: "All time",
};

function DashboardPage() {
  const [range, setRange] = useState<AnalyticsRange>("7d");
  const fetchStats = useServerFn(getAdminDashboardStats);
  const fetchCheckoutMode = useServerFn(getCheckoutMode);
  const checkoutMode = useQuery({
    queryKey: ["admin", "checkout-mode"],
    queryFn: () => fetchCheckoutMode(),
    staleTime: 30_000,
  });

  const { data: crm, isLoading: crmLoading, isError: crmError } = useQuery({
    queryKey: ["admin", "dashboard-stats"],
    queryFn: () => fetchStats(),
    staleTime: 30_000,
  });

  return (
    <div className="space-y-8">
      <AdminPageHeader
        title="Dashboard"
        description="Traffic, engagement and business funnel powered by PostHog."
      />

      <Alert>
        <AlertDescription>
          <span className="font-medium">Checkout mode (read-only):</span>{" "}
          <code className="rounded bg-muted px-1.5 py-0.5 font-semibold uppercase">
            {checkoutMode.isLoading
              ? "loading…"
              : checkoutMode.isError
                ? "unknown"
                : (checkoutMode.data?.mode ?? "unknown")}
          </code>{" "}
          <span className="text-muted-foreground">
            — change this in Meal Plans 2.0 → Admin Preview.
          </span>
        </AlertDescription>
      </Alert>

      <Tabs value={range} onValueChange={(v) => setRange(v as AnalyticsRange)}>
        <TabsList>
          {(Object.keys(RANGE_LABELS) as AnalyticsRange[]).map((r) => (
            <TabsTrigger key={r} value={r}>
              {RANGE_LABELS[r]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* PostHog analytics loads independently; the CRM (database-backed)
          content is passed through so it renders immediately in its
          existing position. */}
      <AnalyticsSections
        range={range}
        crmAlert={
          crmError ? (
            <Alert variant="destructive">
              <AlertDescription>Couldn't load CRM totals.</AlertDescription>
            </Alert>
          ) : null
        }
        crmSection={
          <Section title="Enquiries & subscribers" subtitle="Live totals from the database">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard label="Contact enquiries" value={crm?.contactTotal ?? 0} hint="All-time" loading={crmLoading} />
              <StatCard label="Enquiries — 7 days" value={crm?.contactLast7Days ?? 0} loading={crmLoading} />
              <StatCard label="Newsletter subscribers" value={crm?.newsletterActiveTotal ?? 0} loading={crmLoading} />
              <StatCard label="Delivery-area requests" value={crm?.deliveryTotal ?? 0} loading={crmLoading} />
            </div>
          </Section>
        }
      />
    </div>
  );
}

function AnalyticsSections({
  range,
  crmAlert,
  crmSection,
}: {
  range: AnalyticsRange;
  crmAlert: React.ReactNode;
  crmSection: React.ReactNode;
}) {
  const fetchAnalytics = useServerFn(getAdminAnalytics);
  const analytics = useQuery({
    queryKey: ["admin", "analytics", range],
    queryFn: () => fetchAnalytics({ data: { range } }),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

  const a = analytics.data;
  const loading = analytics.isLoading;
  const notConfigured = a?.configured === false;
  const err = a?.error;

  return (
    <>
      {notConfigured ? (
        <Alert>
          <AlertDescription>
            PostHog is not configured on the server. Add
            <code className="mx-1">POSTHOG_PERSONAL_API_KEY</code>
            and <code className="mx-1">POSTHOG_PROJECT_ID</code> to enable analytics.
          </AlertDescription>
        </Alert>
      ) : err ? (
        <Alert variant="destructive">
          <AlertDescription>PostHog error: {err}</AlertDescription>
        </Alert>
      ) : null}
      {crmAlert}


      {/* Traffic */}
      <Section title="Traffic" subtitle={RANGE_LABELS[range]}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard label="Homepage visits" value={fmtNum(a?.traffic.homepageViews)} loading={loading} />
          <StatCard label="Total pageviews" value={fmtNum(a?.traffic.totalPageviews)} loading={loading} />
          <StatCard label="Unique visitors" value={fmtNum(a?.traffic.uniqueVisitors)} loading={loading} />
          <StatCard label="Sessions" value={fmtNum(a?.traffic.sessions)} loading={loading} />
          <StatCard label="Returning visitors" value={fmtNum(a?.traffic.returningVisitors)} loading={loading} />
          <StatCard
            label="Bounce rate"
            value={a?.traffic.bounceRate == null ? "—" : `${Math.round(a.traffic.bounceRate * 100)}%`}
            hint="Sessions with a single pageview"
            loading={loading}
          />
        </div>
      </Section>

      {/* CRM baseline (always all-time) */}
      {crmSection}



      {/* Top pages */}
      <Section title="Top pages" subtitle="Ranked by pageviews">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ListCard title="Tracked pages" rows={a?.trackedPages ?? []} loading={loading} />
          <ListCard title="All top pages" rows={a?.topPages ?? []} loading={loading} />
        </div>
      </Section>

      {/* Engagement */}
      <Section title="Engagement">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard
            label="Avg session duration"
            value={a?.engagement.avgSessionDurationSec == null ? "—" : fmtDuration(a.engagement.avgSessionDurationSec)}
            loading={loading}
          />
          <StatCard
            label="Pages / session"
            value={a?.engagement.pagesPerSession == null ? "—" : a.engagement.pagesPerSession.toFixed(2)}
            loading={loading}
          />
          <StatCard label="Sessions" value={fmtNum(a?.traffic.sessions)} loading={loading} />
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ListCard title="Top referrers" rows={a?.engagement.topReferrers ?? []} loading={loading} />
          <ListCard title="Device type" rows={a?.engagement.deviceTypes ?? []} loading={loading} />
          <ListCard title="Browser" rows={a?.engagement.browsers ?? []} loading={loading} />
          <ListCard title="Country" rows={a?.engagement.countries ?? []} loading={loading} />
          <ListCard title="City" rows={a?.engagement.cities ?? []} loading={loading} />
        </div>
      </Section>

      {/* Funnel */}
      <Section title="Business funnel" subtitle="Unique visitors that reached each step">
        <FunnelCard rows={a?.funnel ?? []} loading={loading} />
      </Section>

      {/* Placeholder custom events */}
      <Section title="Custom events" subtitle="Placeholders — will populate once instrumented on the site">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {(a?.customEvents ?? []).map((row) => (
            <StatCard key={row.name} label={prettifyEvent(row.name)} value={row.count} loading={loading} />
          ))}
        </div>
      </Section>
    </>
  );
}


function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <header className="flex items-baseline justify-between">
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        {subtitle ? <span className="text-xs text-muted-foreground">{subtitle}</span> : null}
      </header>
      {children}
    </section>
  );
}

function ListCard({
  title,
  rows,
  loading,
}: {
  title: string;
  rows: NamedCount[];
  loading?: boolean;
}) {
  const total = rows.reduce((s, r) => s + r.count, 0);
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-24 animate-pulse rounded bg-muted" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No data in this window.</p>
        ) : (
          <ul className="space-y-1.5">
            {rows.map((r) => {
              const pct = total > 0 ? Math.round((r.count / total) * 100) : 0;
              return (
                <li key={r.name} className="text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-foreground">{r.name}</span>
                    <span className="tabular-nums text-muted-foreground">
                      {r.count.toLocaleString()} · {pct}%
                    </span>
                  </div>
                  <div className="mt-1 h-1 rounded bg-muted">
                    <div className="h-1 rounded bg-primary" style={{ width: `${pct}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function FunnelCard({ rows, loading }: { rows: NamedCount[]; loading?: boolean }) {
  const top = rows[0]?.count ?? 0;
  return (
    <Card>
      <CardContent className="pt-6">
        {loading ? (
          <div className="h-32 animate-pulse rounded bg-muted" />
        ) : (
          <ol className="space-y-3">
            {rows.map((r, i) => {
              const pct = top > 0 ? Math.round((r.count / top) * 100) : 0;
              const prev = i > 0 ? rows[i - 1]!.count : null;
              const stepDrop =
                prev && prev > 0 ? Math.round(((prev - r.count) / prev) * 100) : null;
              return (
                <li key={r.name}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-foreground">
                      {i + 1}. {r.name}
                    </span>
                    <span className="tabular-nums text-muted-foreground">
                      {r.count.toLocaleString()} · {pct}% of entry
                      {stepDrop !== null ? ` · ↓ ${stepDrop}%` : ""}
                    </span>
                  </div>
                  <div className="mt-1 h-2 rounded bg-muted">
                    <div className="h-2 rounded bg-primary" style={{ width: `${pct}%` }} />
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

function fmtNum(n: number | undefined): number | string {
  if (n === undefined) return "—";
  return n.toLocaleString();
}
function fmtDuration(sec: number): string {
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
}
function prettifyEvent(k: string): string {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}